import { Link } from "react-router-dom";
import {
  ArrowRight,
  Boxes,
  Handshake,
  Wrench,
  Headphones,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import LogoMarquee from "@/components/LogoMarquee";
import BrandTile from "@/components/BrandTile";
import ClientTile from "@/components/ClientTile";
import CTABand from "@/components/CTABand";
import CertificationsSection from "@/components/CertificationsSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import Reveal from "@/components/Reveal";
import DownloadProfileButton from "@/components/DownloadProfileButton";
import { PRODUCT_CATEGORIES } from "@/data/products";
import { BRANDS } from "@/data/brands";
import { CLIENTS } from "@/data/clients";
import { COMPANY } from "@/data/company";

const WHAT_WE_DO = [
  {
    Icon: Boxes,
    title: "Single-source industrial supply",
    text: "Lubricants, tooling, abrasives, hardware, safety, chemicals and packaging — from one dependable partner.",
  },
  {
    Icon: Handshake,
    title: "Authorized dealer & distributor",
    text: "Genuine products from leading brands, backed by reliable stock and technical support.",
  },
  {
    Icon: Wrench,
    title: "Custom machines & engineering",
    text: "Application-specific machines sourced through trusted manufacturing partners.",
  },
  {
    Icon: Headphones,
    title: "Technical support & maintenance",
    text: "Lubrication management, condition monitoring and preventive-maintenance guidance.",
  },
];

const WHY_US = [
  "Quality products from authorized brands",
  "Reliable supply chain and timely delivery",
  "Technical expertise and application support",
  "Competitive pricing across categories",
  "Single point of contact for diverse industrial needs",
  "Custom engineering & machines on request",
];

export default function Home() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200)",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="container-nago relative py-24 sm:py-28 lg:py-36 text-white">
          <div className="text-xs sm:text-sm font-bold uppercase tracking-[0.26em] text-red-300">
            {COMPANY.tagline}
          </div>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight max-w-4xl">
            One reliable source for{" "}
            <span className="text-red-400">industrial supply.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-slate-200 text-base sm:text-lg leading-relaxed">
            Lubricants, precision tooling, cutting solutions, safety, packaging
            and custom machines — sourced from leading brands and delivered
            across India, backed by real technical support.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="hero-quote-btn"
            >
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/products"
              className="btn-outline !text-white !border-white hover:!bg-white hover:!text-[#1E3A8A]"
              data-testid="hero-products-btn"
            >
              Explore Products <ChevronRight className="w-4 h-4" />
            </Link>
            <DownloadProfileButton
              variant="light-outline"
              size="lg"
              label="Download Company Profile"
              testId="hero-download-profile-btn"
            />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-slate-900 text-white" data-testid="trust-strip">
        <div className="container-nago py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs sm:text-sm">
          {COMPANY.stats.map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-red-400" />
              <span className="font-semibold">{s.value}</span>
              <span className="text-slate-400">· {s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="section bg-industrial-soft">
        <div className="container-nago">
          <SectionHeading
            eyebrow="What we do"
            title="Industrial supply, engineered around your operations."
            description="From lubrication and precision tooling to safety, packaging and custom machines — Nago International provides quality products and reliable service across India."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHAT_WE_DO.map(({ Icon, title, text }) => (
              <div
                key={title}
                className="card-industrial relative overflow-hidden"
                data-testid={`what-we-do-card-${title.split(" ")[0].toLowerCase()}`}
              >
                <div className="absolute top-0 right-0 w-16 h-16 bg-[#1E3A8A]/5 -translate-y-8 translate-x-8" />
                <div className="w-12 h-12 bg-[#1E3A8A] text-white flex items-center justify-center rounded-xl">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display font-bold text-lg text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="section bg-white" data-testid="product-categories">
        <div className="container-nago">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <SectionHeading
              eyebrow="Product categories"
              title="A complete industrial portfolio."
              description="Nine focused categories, sourced from leading manufacturers, tuned for productive manufacturing and engineering."
            />
            <Link
              to="/products"
              className="text-[#1E3A8A] hover:text-[#E30613] font-semibold text-sm inline-flex items-center gap-1"
              data-testid="view-all-products"
            >
              View all products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCT_CATEGORIES.map(
              ({ slug, title, short, Icon, image }, i) => (
                <Reveal key={slug} delay={(i % 3) * 80}>
                  <Link
                    to={`/products/${slug}`}
                    className="group card-industrial p-0 overflow-hidden flex flex-col h-full"
                    data-testid={`product-card-${slug}`}
                  >
                    <div className="relative h-44 overflow-hidden bg-white p-4 flex items-center justify-center">
                      <img
                        src={image}
                        alt={title}
                        loading="lazy"
                        className="max-h-full max-w-full object-contain group-hover:scale-105"
                        style={{ transition: "transform 500ms ease" }}
                      />
                      <div className="absolute top-3 left-3 w-11 h-11 bg-[#1E3A8A] text-white flex items-center justify-center rounded-xl shadow-md">
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-display font-bold text-lg text-slate-900 leading-snug">
                        {title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                        {short}
                      </p>
                      <div className="mt-4 inline-flex items-center text-sm font-semibold text-[#1E3A8A] group-hover:text-[#E30613]">
                        Explore <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ),
            )}
          </div>
        </div>
      </section>

      {/* BRANDS CAROUSEL */}
      <section className="section bg-slate-50" data-testid="brands-section">
        <div className="container-nago">
          <Reveal>
            <SectionHeading
              eyebrow="Brands we represent"
              title="Authorized dealer for industry-leading names."
              description="We work as authorized dealers, distributors and channel partners for reputable industrial brands — ensuring genuine products and reliable technical backing."
              align="center"
            />
          </Reveal>
        </div>
        <div className="mt-12">
          <LogoMarquee
            items={BRANDS}
            testId="brands-marquee"
            renderItem={(b) => (
              <BrandTile text={b.name} sub={b.category} image={b.logo} />
            )}
          />
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/brands"
            className="btn-outline text-sm"
            data-testid="view-all-brands"
          >
            View all brands <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section bg-white" data-testid="why-us">
        <div className="container-nago grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <SectionHeading
              eyebrow="Why choose Nago International"
              title="Built for uptime. Priced for productivity."
              description="Since 2016, we've helped Indian manufacturers, foundries and engineering enterprises get the right products with dependable service — every time."
            />
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {WHY_US.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <div className="mt-1 w-5 h-5 shrink-0 bg-[#E30613] text-white flex items-center justify-center rounded-md">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-slate-700 text-sm">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100} y={30}>
            <div className="relative">
              <div
                className="relative aspect-[4/5] bg-cover bg-center rounded-2xl"
                style={{
                  backgroundImage:
                    "url(https://images.unsplash.com/photo-1727292486169-33eba0865c99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
                }}
              >
                <div className="absolute -bottom-6 -left-6 bg-[#E30613] text-white p-6 max-w-[220px] rounded-xl shadow-xl hidden sm:block">
                  <div className="font-display font-extrabold text-3xl leading-none">
                    10+
                  </div>
                  <div className="mt-2 text-sm">
                    Authorized industrial brands under one roof
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 bg-[#1E3A8A] text-white p-6 max-w-[200px] rounded-xl shadow-xl hidden sm:block">
                  <div className="font-display font-extrabold text-3xl leading-none">
                    Pan-India
                  </div>
                  <div className="mt-2 text-sm">Supply reach from Belagavi</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLIENTS CAROUSEL */}
      <section className="section bg-slate-50" data-testid="clients-section">
        <div className="container-nago">
          <Reveal>
            <SectionHeading
              eyebrow="Trusted by industry"
              title="Serving manufacturers, foundries and engineering enterprises across India."
              align="center"
            />
          </Reveal>
        </div>
        <div className="mt-12">
          <LogoMarquee
            items={CLIENTS}
            speed="slow"
            testId="clients-marquee"
            renderItem={(c) => (
              <ClientTile short={c.short} name={c.name} image={c.image} />
            )}
          />
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/clients"
            className="btn-outline text-sm"
            data-testid="view-all-clients"
          >
            View all clients <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <TestimonialsSection />

      {/* <CertificationsSection /> */}

      <CTABand
        title="Tell us what you need — we'll source it."
        subtitle="From a single grade of hydraulic oil to a full turnkey machine — our team will get back to you with the right product, the right pack and the right price."
      />
    </div>
  );
}
